import { type CampaignChannel, campaignChannel } from '@jobilla/nexus-sdk'
import { GlobeIcon, MegaphoneIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export type ChannelSelectorProps = {
	channels: CampaignChannel[]
	onChange: (channels: CampaignChannel[]) => void
	careerSiteEnabled: boolean
}

export function ChannelSelector({ channels, onChange, careerSiteEnabled }: ChannelSelectorProps) {
	const { t } = useTranslation('editor')

	const isMetaSelected = channels.includes(campaignChannel.META)
	const isCareerSiteSelected = channels.includes(campaignChannel.CAREER_SITE)

	const toggle = (channel: CampaignChannel) => {
		const isSelected = channels.includes(channel)

		if (isSelected) {
			const remaining = channels.filter((c) => c !== channel)
			if (remaining.length === 0) { return }
			onChange(remaining)
		} else {
			onChange([...channels, channel])
		}
	}

	return (
		<div className="flex flex-col gap-2">
			<div className="text-sm font-medium text-grey-700">{t('review.channels')}</div>
			<div className="grid grid-cols-2 gap-3">
				<button
					type="button"
					onClick={() => toggle(campaignChannel.META)}
					className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors text-left ${
						isMetaSelected
							? 'border-brand-500 bg-brand-50'
							: 'border-grey-200 bg-grey-50 hover:border-grey-300'
					}`}
				>
					<div
						className={`p-2.5 rounded-lg ${
							isMetaSelected ? 'bg-brand-100 text-brand-600' : 'bg-grey-200 text-grey-500'
						}`}
					>
						<MegaphoneIcon className="size-5" />
					</div>
					<div>
						<div className="font-medium text-grey-900">{t('review.channelMeta')}</div>
						<div className="text-sm text-grey-500">{t('review.channelMetaDescription')}</div>
					</div>
				</button>

				{careerSiteEnabled && (
					<button
						type="button"
						onClick={() => toggle(campaignChannel.CAREER_SITE)}
						className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors text-left ${
							isCareerSiteSelected
								? 'border-purple-500 bg-purple-50'
								: 'border-grey-200 bg-grey-50 hover:border-grey-300'
						}`}
					>
						<div
							className={`p-2.5 rounded-lg ${
								isCareerSiteSelected ? 'bg-purple-100 text-purple-600' : 'bg-grey-200 text-grey-500'
							}`}
						>
							<GlobeIcon className="size-5" />
						</div>
						<div>
							<div className="font-medium text-grey-900">{t('review.channelCareerSite')}</div>
							<div className="text-sm text-grey-500">{t('review.channelCareerSiteDescription')}</div>
						</div>
					</button>
				)}
			</div>
		</div>
	)
}
